import { UnsavedChangesGuard } from './../../shared-components/guards/unsaved-changes/unsaved-changes.guard';
import { CheckForErrorsGuard } from './../../shared-components/guards/check-for-errors/check-for-errors.guard';
import { CourseHasProfile } from './models/course-has-profile';
import { LoginService } from './../../shared-modules/login/login.service';
import { map } from 'rxjs/operators';
import { System } from './../../shared-components/System';
import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin as observableForkJoin, Observable, Subscription, Subject, BehaviorSubject } from 'rxjs';
import { ClassroomSummary } from './models/classroom-summary.model';
import { YearService } from '../../shared-components/year/year.service';
import { UserService } from '../../shared-components/user/user.service';
import { ClassroomTerm } from './models/classroom-term.model';
import { Year } from '../../shared-components/year/year.model';
import { Portal } from '../../shared-components/portal.model';
import { UserPreferencesService } from '../../shared-components/user-preferences/user-preferences.service';
import { ClassroomSummaryFilter } from './models/classroom-summary-filter';
import { formatDate } from '@angular/common';
import { FilterModel } from '../../shared-components/controls/filter/filter.model';
import { Attachment } from '../../shared-components/models/attachment.model';

@Injectable()
export class ClassroomSummaryService {

    displayedBulletins = false; // Announcements modal that displays at first login of the day.
    private _selectedCourse: ClassroomSummary;
    public get selectedCourse(): ClassroomSummary {
        return this._selectedCourse;
    }
    public set selectedCourse(value: ClassroomSummary) {
        const canContinue = this.unsavedChangesGuard.checkForDeactivation() && this.checkForErrorsGuard.checkForDeactivation();
        if (canContinue instanceof Observable) {
            canContinue.subscribe(clickedOk => {
                if (clickedOk) {
                    this._selectedCourse = value;
                    this.observableselectedCourse.next(this._selectedCourse);
                }
            });
        } else if (canContinue) {
            this._selectedCourse = value;
            this.observableselectedCourse.next(this._selectedCourse);
        }
    }

    public get selectedCourseSummary(): string {
        return this.getCourseSummary(this.selectedCourse);
    }
    private _myClasses: ClassroomSummary[];
    public get myClasses(): ClassroomSummary[] {
        return this._myClasses;
    }
    public set myClasses(value: ClassroomSummary[]) {
        this._myClasses = value;
        this.observableMyClasses.next(value);
    }
    public observableMyClasses = new BehaviorSubject<ClassroomSummary[]>([]);
    observableMyClassesStream$ = this.observableMyClasses.asObservable();
    private previousArgs: any;
    gradebookClassesSet = false;

    public terms: ClassroomTerm[];

    StaffAssignedPortals: Portal[];
    defaultYear: Year;
    defaultYearSubscription: Subscription;
    public observableselectedCourse = new Subject<ClassroomSummary>();
    observableselectedCourseStream$ = this.observableselectedCourse.asObservable();

    filterData: ClassroomSummaryFilter;
    classSourceFilters: FilterModel[];
    filterChange = new EventEmitter<any>();

    timeStamp: Date = new Date();
    refreshInterval: Subscription;
    modelAge = 0;
    lastRefresh: string;
    loadedYear: number;
    mergeModalOpened: boolean;
    gradebookClasses: ClassroomSummary[];

    private _selectedTerm: ClassroomTerm;
    public get selectedTerm(): ClassroomTerm {
        return this._selectedTerm;
    }
    public set selectedTerm(value: ClassroomTerm) {
        this._selectedTerm = value;
        this.observableSelectedTerm.next(this._selectedTerm);
    }
    observableSelectedTerm = new Subject<ClassroomTerm>();
    observableSelectedTermStream$ = this.observableSelectedTerm.asObservable();

    viewOptions = [
        {
            value: '1',
            display: 'Unmerged'
        },
        {
            value: '2',
            display: 'Merged For Attendance'
        },
        {
            value: '3',
            display: 'Merged For Grading'
        }
    ];
    private _selectedView = this.viewOptions[0];
    public get selectedView() {
        return this._selectedView;
    }
    public set selectedView(value) {
        this._selectedView = value;
        this.observableSelectedView.next(this._selectedView);
    }
    observableSelectedView = new Subject<any>();
    observableSelectedViewStream$ = this.observableSelectedView.asObservable();


    constructor(private http: HttpClient,
        private yearService: YearService,
        private userService: UserService,
        private loginService: LoginService,
        private userPreferencesService: UserPreferencesService,
        private checkForErrorsGuard: CheckForErrorsGuard,
        private unsavedChangesGuard: UnsavedChangesGuard) {
        this.defaultYear = this.yearService.defaultYear;
        this.defaultYearSubscription = yearService.observableDefaultYearStream$.subscribe(
            defaultYear => {
                this.defaultYear = defaultYear;
            }
        );
    }

    organizeClasses(courses: ClassroomSummary[]) {
        // POST organizes the courses properties
        courses.forEach(course => {
            if (!course.CourseSection) {
                course.CourseSection = course.SubjectCode + '-' + course.Section;
            }
            if (!course.CourseMenuText) {
                course.CourseMenuText = this.getCourseSummary(course);
            }
            if (course.TermToDate) {
                course.TermToDate = new Date(course.TermToDate);
            }
            if (course.TermFromDate) {
                course.TermFromDate = new Date(course.TermFromDate);
            }
        });
    }

    getMyClasses(termId: number, viewId: number, isTeacher: boolean, includeAllCourses: boolean, dateItem: Date): Observable<ClassroomSummary[]> {
        if (this.previousArgs &&
            this.previousArgs.termId == termId &&
            this.previousArgs.viewId == viewId &&
            this.previousArgs.isTeacher == isTeacher &&
            this.previousArgs.includeAllCourses == includeAllCourses &&
            this.previousArgs.dateItem == dateItem &&
            this.previousArgs.yearId == this.defaultYear.Id &&
            this.previousArgs.school == this.defaultYear.SchoolID) {
            return this.observableMyClassesStream$;
        } else {
            this.previousArgs = {
                termId: termId,
                viewId: viewId,
                isTeacher: isTeacher,
                includeAllCourses: includeAllCourses,
                dateItem: dateItem,
                year: this.defaultYear.Id,
                school: this.defaultYear.SchoolID
            };
        }
        const currentDate = new Date();
        const dateString = (currentDate.getUTCMonth() + 1) + '/' + currentDate.getDate() + '/' + currentDate.getUTCFullYear();
        let queryString = '';
        queryString += '?schoolId=' + this.defaultYear.SchoolID +
            '&yearId=' + this.defaultYear.Id +
            '&termId=' + termId +
            '&viewId=' + viewId +
            '&isTeacher=' + isTeacher +
            '&dateItem=' + dateItem +
            '&includeAllCourses=' + includeAllCourses +
            '&currentDate=' + dateString;
        if (!this.mergeModalOpened) {
            this.myClasses = null;
        }
        return this.http.get<ClassroomSummary[]>(System.apiURL.Classroom + 'GetMyClasses' + queryString).pipe(
            map(result => {
                this.organizeClasses(result);
                if (!this.mergeModalOpened) {
                    this.myClasses = result;
                }
                this.loadedYear = this.defaultYear.Id;
                return result;
            })
        );
    }

    getClassesForSchoolAndYear(yearId: number, schoolId: number, termId: number, viewId: number): Observable<ClassroomSummary[]> {
        let queryString = '';
        queryString += '?schoolId=' + schoolId +
            '&yearId=' + yearId +
            '&termId=' + termId +
            '&viewId=' + viewId +
            '&isTeacher=' + true +
            '&dateItem=' + new Date() +
            '&includeAllCourses=' + false +
            '&currentDate=' + formatDate(new Date, 'MM/dd/yyyy', 'en-US');
        return this.http.get<ClassroomSummary[]>(System.apiURL.Classroom + 'GetMyClasses' + queryString).pipe(
            map(result => {
                this.myClasses = result;
                this.organizeClasses(this.myClasses);
                return this.myClasses;
            })
        );
    }

    getClassesWithoutBroadcast(yearId: number, schoolId: number, termId: number, viewId: number): Observable<ClassroomSummary[]> {
        let queryString = '';
        queryString += '?schoolId=' + schoolId +
            '&yearId=' + yearId +
            '&termId=' + termId +
            '&viewId=' + viewId +
            '&isTeacher=' + true +
            '&dateItem=' + new Date() +
            '&includeAllCourses=' + false +
            '&currentDate=' + formatDate(new Date, 'MM/dd/yyyy', 'en-US');
        return this.http.get<ClassroomSummary[]>(System.apiURL.Classroom + 'GetMyClasses' + queryString).pipe(
            map(courses => {
                this.organizeClasses(courses);
                return courses;
            })
        );
    }


    getGradebookAttendanceClasses(forGradebook: boolean, termId: number) {
        const currentDate = new Date();
        const dateString = (currentDate.getUTCMonth() + 1) + '/' + currentDate.getDate() + '/' + currentDate.getUTCFullYear();
        let queryString = '';
        const viewId = (forGradebook) ? 3 : 2;
        queryString += '?schoolId=' + this.defaultYear.SchoolID +
            '&yearId=' + this.defaultYear.Id +
            '&termId=' + termId +
            '&viewId=' + viewId +
            '&isTeacher=' + this.userService.currentUser.UserIsTeacher +
            '&dateItem=' + null +
            '&includeAllCourses=' + false +
            '&currentDate=' + dateString;
        return this.http.get<ClassroomSummary[]>(System.apiURL.Classroom + 'GetMyClasses' + queryString).pipe(
            map(courses => {
                this.organizeClasses(courses);
                return courses;
            })
        );
    }

    getCourseProfileExistence(ssecIds: number[]) {
        return this.http.post<CourseHasProfile[]>(System.apiURL.Classroom + 'GetCourseHasProfile', ssecIds);
    }

    getClassroomTermsAndPreferences(): Observable<any> {
        let paramString = '';
        paramString += '?studentId=0';
        paramString += '&pageURL=/OneSISClassroomSummary';
        paramString += '&subjectListId=0';
        paramString += '&subjectSection=';
        paramString += '&isDefaultPreferences=' + false;
        return observableForkJoin(
            this.http.get(System.apiURL.Classroom + 'GetClassroomTerms?schoolId=' + this.defaultYear.SchoolID + '&yearId=' + this.defaultYear.Id),
            this.http.get(System.apiURL.Student + 'GetUserPreferences' + paramString),
            (terms, preferences) => ({ terms, preferences })
        );
    }

    getClassroomPreferences(): Observable<any> {
        let paramString = '';
        paramString += '?studentId=0';
        paramString += '&pageURL=/OneSISClassroomSummary';
        paramString += '&subjectListId=0';
        paramString += '&subjectSection=';
        paramString += '&isDefaultPreferences=' + false;
        return this.http.get(System.apiURL.Student + 'GetUserPreferences' + paramString);
    }

    mergeGradingSections(tgsId: number, mergeSectionName: string, primarySection: number, mergedIds: string) {
        return this.http.get<string>(System.apiURL.Classroom + 'MergeGradingSections?tgsId=' + tgsId + '&mergeSectionName=' + mergeSectionName + '&primarySection=' + primarySection + '&mergedIds=' + mergedIds);
    }

    unmergeGradingSections(tgsId: number, mergeId: number) {
        return this.http.get<string>(System.apiURL.Classroom + 'UnmergeGradingSections?tgsId=' + tgsId + '&mergeSSEC_ID=' + mergeId);
    }

    mergeAttendanceSections(tgsId: number, mergeSectionName: string, primarySection: number, mergedIds: string) {
        return this.http.get<any>(System.apiURL.Classroom + 'MergeAttendanceSections?tgsId=' + tgsId + '&mergeSectionName=' + mergeSectionName + '&primarySection=' + primarySection + '&mergedIds=' + mergedIds);
    }

    unmergeAttendanceSections(mergeId: number) {
        return this.http.get(System.apiURL.Classroom + 'UnmergeAttendanceSection?mergedId=' + mergeId);
    }

    myClassesExportToExcel(sheetName: string, includeHeaders: boolean, classes: ClassroomSummary[]) {
        return this.http.post<string>(System.apiURL.Classroom + 'MyClassesExportToExcel?worksheetName=' + sheetName + '&includeColumnHeading=' + includeHeaders, classes);
    }

    getCourseSummary(course: ClassroomSummary): string {
        let courseSummary = '';
        if (course != null) {
            courseSummary = course.Course + ' (' + course.SubjectCode + '-' + course.Section + ') ';
            if (this.loginService.LoginSettings.V10) {
                if (course.TermTo == course.TermFrom) {
                    courseSummary += course.TermTo + ' ';
                } else {
                    courseSummary += course.TermFrom + '-' + course.TermTo + ' ';
                }
                courseSummary += 'M: ' + course.Meets;
            }
            else {
                courseSummary += course.Meets;
            }
        }
        return courseSummary;
    }

    getTeacherClassesByMergedId(schoolId, yearId, termId, viewId, mergedIds, dateItem) {
        const queryString = 'schoolId=' + schoolId +
            '&yearId=' + yearId +
            '&termId=' + termId +
            '&viewId=' + viewId +
            '&dateItem=' + dateItem;
        return this.http.post<ClassroomSummary[]>(System.apiURL.Classroom + 'GetTeacherClassesByMergedId?' + queryString, mergedIds);
    }

    getStudentCourses(studentId: number, ssecId: number, yearId: number) {
        return this.http.get<ClassroomSummary[]>(System.apiURL.Classroom + 'GetSectionByTeacherAndStudent?studentId=' + studentId + '&ssecId=' + ssecId + '&yearId=' + yearId);
    }

    getLastRefresh() {
        const currentTime = new Date();
        this.modelAge = Math.round((currentTime.getTime() - this.timeStamp.getTime()) / 60000);
        return this.modelAge;
    }

    clearRefreshTimer() {
        this.timeStamp = new Date();
    }

    setTimer58() {
        this.timeStamp = new Date();
        this.timeStamp.setMinutes(this.timeStamp.getMinutes() - 59);
    }

    editCourseInfo(operation: string, courseId: number, nickname: string, attachment: Attachment) {
        return this.http.post<Attachment[]>(System.apiURL.Classroom + 'EditCourseInfo?operation=' + operation + '&courseId=' + courseId + '&nickname=' + nickname, attachment);
    }

    getMergeSubjectSectionData(subjectSectionIds: number[]) {
        return this.http.post<any>(System.apiURL.Classroom + 'GetMergeSubjectSectionData', subjectSectionIds);
    }

}
